// i18n
import Skeleton from 'presentations/views/components/atoms/Skeleton';
import { translateInComponent } from 'infras/i18n';
import translations from './i18n.json';
import Link from 'presentations/views/components/atoms/Link';
import styled from '@emotion/styled';
import {
  black,
  blackFix,
  blueGrayPale,
  gray01,
  primary,
  white,
  whiteFix,
} from 'presentations/views/styles/color';
import DashboardPageTemplate from 'presentations/views/components/templates/DashboardPageTemplate';
import ServiceList from 'presentations/views/components/organisms/ServiceList';
import HelpList from 'presentations/views/components/organisms/HelpList';
import ConfirmationDialog from 'presentations/views/components/molecules/ConfirmationDialog';
import FormPaidIcon from 'presentations/views/components/molecules/FormPaidIcon';
import {
  spacing,
  fontSize,
  mediaMax,
  borderRadius,
  fontWeight,
  mediaMaxWithLayoutTheme,
  isMediaMax,
} from 'presentations/views/styles';
import Button from 'presentations/views/components/atoms/Button';
import Icon from 'presentations/views/components/atoms/Icon';
import { ViewerItem } from 'presentations/api/schema/user';
import Pagination from 'presentations/views/components/atoms/Pagination';
import { useServiceListPage } from './hooks';
import Image from 'presentations/views/components/atoms/Image';
import { useContext } from 'react';
import DashboardServiceNavigation, {
  SectionAdminView,
  SectionMemberView,
} from 'presentations/views/components/organisms/DashboardServiceNavigation';
import { DashboardPortalContext } from 'presentations/reducers/DashboardPortal';
import {
  feeHelpPath,
  helpCollectionPath,
  helpPath,
  pricingPath,
  servicePaymentPath,
} from 'presentations/routes/path';
import Headline from 'presentations/views/components/atoms/Headline';
import { ShopAccountStatus } from 'domains/models/team_shop_account/status';
import { Role } from 'domains/models/team_user/role';
import ServiceEventTypeListDialog from '../../organisms/ServiceEventTypeListDialog';
import PaymentFeature from 'domains/models/subscription/features/payment';

interface Props {
  viewer?: ViewerItem;
}

export default function Component(props: Props) {
  const t = translateInComponent(translations);
  const { state: dashboardState } = useContext(DashboardPortalContext);
  const viewer = props.viewer || dashboardState.viewer!;
  const { state, actions } = useServiceListPage({
    viewer,
  });

  const isMobile = isMediaMax('desktop');
  const flows = isMobile
    ? [
        [1, 2],
        [3, 4],
      ]
    : [[1, 2, 3, 4]];
  const questions = [
    { title: t(`Question.0`), path: helpPath('7155136') },
    { title: t(`Question.1`), path: helpPath('7155135') },
    { title: t(`Question.2`), path: helpPath('7155135#h_2035377a30') },
    { title: t(`Question.3`), path: feeHelpPath },
    { title: t(`Question.5`), path: helpPath('7155134') },
    { title: t(`Question.6`), path: helpPath('7155133') },
  ];
  const paymentFeature = new PaymentFeature({
    planVersion: viewer.planVersion,
  });

  return (
    <DashboardPageTemplate
      title={t('title')}
      viewer={viewer}
      isVisibleSupport
      customHeader={
        <DashboardServiceNavigation
          viewer={viewer}
          activeIndex={
            viewer.role > Role.Admin
              ? SectionMemberView.home
              : SectionAdminView.home
          }
        />
      }
      tabBarContent={
        <Container>
          {viewer.role <= Role.Admin && (
            <>
              {state.isAccountLoading ? (
                <StartContainer>
                  <StartInnerContainer>
                    <BoxLeft>
                      <Skeleton width="100px" variant="text" />
                      <Skeleton width="100%" variant="text" />
                    </BoxLeft>
                    <BoxRight>
                      <Skeleton width="100px" variant="text" />
                      <Skeleton width="100%" variant="text" />
                    </BoxRight>
                  </StartInnerContainer>
                </StartContainer>
              ) : (
                (!state.shopAccount ||
                  state.shopAccount.status === ShopAccountStatus.Disable) && (
                  <StartContainer>
                    <StartInnerContainer>
                      <BoxLeft>
                        <StartTitle>
                          {t('Start title')}
                          {!paymentFeature.isValid(viewer.planId) && (
                            <PaidLink href={pricingPath()}>
                              <FormPaidIcon
                                plan={paymentFeature.validPlanId()}
                              />
                            </PaidLink>
                          )}
                        </StartTitle>
                        <StartDesc>{t('Start desc')}</StartDesc>

                        <StartButton>
                          <StartButtonInner
                            isDisabled={!paymentFeature.isValid(viewer.planId)}
                            size={'large'}
                            type="submit"
                            color="primary"
                            href={servicePaymentPath()}
                          >
                            {t('Start button')}
                          </StartButtonInner>
                        </StartButton>
                        <StartButtonDesc>
                          {t('Start button desc.0')}
                          <Link href={feeHelpPath} target="_blank">
                            {t('Start button desc.1')}
                            <Icon
                              name="checkbox-multiple-blank-outline"
                              size={fontSize(14)}
                            />
                          </Link>
                        </StartButtonDesc>
                      </BoxLeft>
                      <BoxRight>
                        <ServiceFlow>
                          <ServiceFlowTitle>{t('flowTitle')}</ServiceFlowTitle>
                          {flows.map((flowRow, index) => (
                            <ServiceFlowContainer
                              isShowBorder={isMobile && index === 0}
                            >
                              {flowRow.map((flowColumn) => (
                                <>
                                  <ServiceFlowItem>
                                    <ServiceFlowItemImageContainer>
                                      <Image
                                        src={`pages/dashboard/services-flow${flowColumn}.svg`}
                                        alt={t(`flow desc ${flowColumn}`)}
                                      />
                                    </ServiceFlowItemImageContainer>
                                    <ServiceFlowItemDescContainer>
                                      <FlowNumberContainer>
                                        <FlowNumber>{flowColumn}</FlowNumber>
                                      </FlowNumberContainer>
                                      <ServiceFlowItemDesc>
                                        {t(`flow desc ${flowColumn}`)}
                                      </ServiceFlowItemDesc>
                                    </ServiceFlowItemDescContainer>
                                  </ServiceFlowItem>
                                  {flowColumn !==
                                    flowRow[flowRow.length - 1] && (
                                    <AllowIcon name="chevron-right" size={2} />
                                  )}
                                </>
                              ))}
                            </ServiceFlowContainer>
                          ))}
                          <ServiceFlowDesc>{t('flowDesc')}</ServiceFlowDesc>
                        </ServiceFlow>
                      </BoxRight>
                    </StartInnerContainer>
                  </StartContainer>
                )
              )}
            </>
          )}

          <InnerContainer>
            <Main>
              <HostEventTypeListContainer>
                <ActionContainer>
                  <AvatarContainer>
                    <ProfileName>{t('Service')}</ProfileName>
                  </AvatarContainer>
                  {viewer.role <= Role.Admin && (
                    <ButtonWrap
                      variant="text"
                      size="small"
                      color="primary"
                      onClick={actions.onClickNewService}
                      dataProps={{
                        'data-intercom-target': 'service-create-button',
                      }}
                    >
                      <ButtonIcon name="add" />
                      {t('Create')}
                    </ButtonWrap>
                  )}
                </ActionContainer>

                <ServiceList
                  isLoading={state.isLoading}
                  viewer={viewer}
                  services={state.services}
                  onSelectMenu={actions.onSelectMenu}
                  onClickShowEventTypes={actions.onClickShowEventTypes}
                />
              </HostEventTypeListContainer>
              {state.numberOfPages > 1 && (
                <PaginationContainer>
                  <Pagination
                    currentPage={state.currentPage}
                    numberOfPages={state.numberOfPages}
                    onChange={actions.onChangePage}
                  />
                </PaginationContainer>
              )}
              <ConfirmationDialog
                isLoading={state.isConfirmLoading}
                isOpen={state.deleteService !== null}
                onSubmit={actions.onDeleteService}
                onCancel={actions.onCloseDialog}
                title={t('Delete?')}
                submitLabel={t('Delete')}
              />
              {state.showEventTypeService && (
                <ServiceEventTypeListDialog
                  viewer={viewer}
                  eventTypes={state.showEventTypeService.eventTypes}
                  onClose={actions.onCloseEventTypeServiceDialog}
                />
              )}
            </Main>
            <Sub>
              <BoxHeader>
                <BoxHeaderIcon name="bulb-on-outline" />
                <BoxHeadline variant="h2">{t('Help')}</BoxHeadline>
              </BoxHeader>
              <HelpList questions={questions} />
              <ViewAllLink href={helpCollectionPath('3910361')} target="_blank">
                <HelpIcon name="help" color="primary" />
                <ViewAllLabel>{t('View all')}</ViewAllLabel>
              </ViewAllLink>
            </Sub>
          </InnerContainer>
        </Container>
      }
    />
  );
}

const Container = styled.div``;
const InnerContainer = styled.div`
  display: flex;
  ${mediaMax.tablet} {
    flex-direction: column;
  }
  padding-bottom: ${spacing(24)};
`;
const Main = styled.div`
  flex: 1;
  margin-right: ${spacing(24)};
  ${mediaMax.tablet} {
    margin-right: 0;
  }
`;
const Sub = styled.div`
  width: 290px;
  ${mediaMax.tablet} {
    margin-top: ${spacing(24)};
    width: 100%;
    padding: 0 ${spacing(12)};
  }
`;

const HostEventTypeListContainer = styled.div`
  margin-top: ${spacing(48)};
  padding-left: ${spacing(24)};
  ${mediaMax.tablet} {
    margin-top: ${spacing(24)};
    padding: 0 ${spacing(12)};
  }
`;

const ProfileName = styled.div`
  font-size: ${fontSize(24)};
  font-weight: bold;
  ${mediaMax.tablet} {
    font-size: ${fontSize(18)};
  }
`;
const ActionContainer = styled.div`
  margin: 0 auto ${spacing(24)};
  display: flex;
  justify-content: space-between;
  text-align: right;
  ${mediaMax.tablet} {
    margin-bottom: ${spacing(20)};
  }
`;
const ButtonWrap = styled(Button)`
  width: 230px;

  ${mediaMax.tablet} {
    width: auto;
  }
`;
const ButtonIcon = styled(Icon)`
  margin-right: ${spacing(8)};
`;

const PaginationContainer = styled.div`
  padding: ${spacing(48)} 0;
  display: flex;
  justify-content: center;
`;

const StartContainer = styled.div`
  min-height: 100%;
  padding: ${spacing(32)};
  background-color: ${white};
  border-radius: ${borderRadius(16)};
  margin: ${spacing(24)} ${spacing(24)};

  ${mediaMax.tablet} {
    padding: ${spacing(20)} ${spacing(12)};
    margin: ${spacing(16)} ${spacing(12)};
  }
`;
const StartInnerContainer = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;

  ${mediaMax.tablet} {
    flex-direction: column;
  }
`;
const BoxLeft = styled.div`
  flex-basis: 40%;
  min-width: 40%;
  padding: 0 0 0 ${spacing(40)};

  :first-of-type {
    padding: 0 ${spacing(40)} 0 0;

    ${mediaMax.tablet} {
      padding: ${spacing(32)} 0 0 0;
    }
  }

  ${mediaMax.tablet} {
    padding: 0;
    margin-bottom: ${spacing(32)};
  }
`;

const BoxRight = styled.div`
  flex-basis: 60%;
  min-width: 60%;
  padding: 0 0 0 ${spacing(40)};

  :first-of-type {
    padding: 0 ${spacing(40)} 0 0;

    ${mediaMax.tablet} {
      padding: ${spacing(32)} 0 0 0;
    }
  }

  ${mediaMax.tablet} {
    padding: 0;
    margin-bottom: ${spacing(32)};
  }
`;

const StartTitle = styled.div`
  font-size: ${spacing(16)};
  font-weight: bold;
  margin-bottom: ${spacing(16)};
`;
const StartDesc = styled.div`
  font-size: ${spacing(14)};
  margin-bottom: ${spacing(16)};
`;
const StartButton = styled.div`
  margin-bottom: ${spacing(16)};
`;
const StartButtonInner = styled(Button)<{
  themeColor?: string;
  isThemeColorLight?: boolean;
}>`
  width: 240px;
  :first-of-type {
    margin-left: 0;
  }

  background-color: ${(props) => props.themeColor || ''};
  color: ${(props) => (props.isThemeColorLight ? blackFix : whiteFix)};
  :hover {
    background-color: ${(props) => props.themeColor || ''};
    color: ${(props) => (props.isThemeColorLight ? blackFix : whiteFix)};
  }

  ${mediaMax.tablet} {
    width: 100% !important;
  }
`;
const StartButtonDesc = styled.div`
  font-size: ${spacing(12)};
`;

const BoxHeader = styled.div`
  display: flex;
  align-items: center;
  margin: ${spacing(48)} 0 ${spacing(24)};

  ${mediaMax.tablet} {
    margin: ${spacing(24)} 0 ${spacing(20)};
  }
`;
const BoxHeaderIcon = styled(Icon)`
  margin-right: ${spacing(8)};
  color: ${primary};

  ${mediaMax.tablet} {
    margin-right: ${spacing(4)};
  }
`;
const BoxHeadline = styled(Headline)`
  font-size: ${fontSize(18)};

  ${mediaMax.tablet} {
    font-size: ${fontSize(18)};
  }
`;
const AvatarContainer = styled.div`
  display: flex;
  align-items: center;
  ${mediaMax.tablet} {
    /* margin-bottom: ${spacing(24)}; */
  }
`;

const ServiceFlow = styled.div`
  background-color: ${blueGrayPale};
  padding: ${spacing(24)};
  border-radius: 16px;
`;
const ServiceFlowTitle = styled.div`
  text-align: center;
  font-size: ${fontSize(16)};
  font-weight: bold;
`;
const ServiceFlowContainer = styled.div<{
  isShowBorder?: boolean;
}>`
  display: flex;
  align-items: start;
  justify-content: center;
  padding: ${spacing(24)} 0;

  border-width: ${(props) => (props.isShowBorder ? '1px' : 0)};
  border-style: solid;
  border-color: gray05;
`;
const ServiceFlowItem = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-right: ${spacing(14)};
`;
const ServiceFlowDesc = styled.div`
  font-size: ${fontSize(12)};
  margin-top: ${spacing(8)};
`;
const ServiceFlowItemImageContainer = styled.div`
  display: flex;
  align-items: end;
  min-height: 62px;
`;
const ServiceFlowItemDescContainer = styled.div`
  display: flex;
  align-items: center;
  margin-top: ${spacing(24)};
`;
const FlowNumberContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 24px;
  height: 24px;
  border-radius: 50px;
  background-color: ${primary};
  margin-right: ${spacing(8)};
`;
const FlowNumber = styled.div`
  font-size: ${fontSize(12)};
  color: ${whiteFix};
  width: 24px;
  text-align: center;
  font-weight: bold;
`;
const AllowIcon = styled(Icon)`
  width: 12px;
  height: 20px;
  color: ${primary};
  margin-right: ${spacing(14)};
  align-self: center;
  ${mediaMax.tablet} {
    display: none;
  }
`;
const ServiceFlowItemDesc = styled.span`
  color: ${gray01};
  font-size: ${fontSize(12)};
  font-weight: bold;
`;

const ViewAllLink = styled(Link)`
  display: flex;
  align-items: center;
  margin-top: ${spacing(16)};
`;
const HelpIcon = styled(Icon)`
  width: 20px !important;
  height: 20px !important;
  margin-right: ${spacing(6)};
`;
const ViewAllLabel = styled.span`
  font-size: ${fontSize(12)};
  font-weight: ${fontWeight('normal')};
  color: ${black};
  line-height: 20px;
  vertical-align: center;
`;

const PaidLink = styled(Link)`
  display: block;
  margin-top: ${spacing(4)};
`;
